import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { broadcastQueueApi } from '@/api/broadcastQueue';
import { TestSelfSection } from '@/components/features/delivery/TestSelfSection';
import { isDemoNotify } from '@/demo/isDemoNotify';
import type { ChannelOption, SendTestCloneResponse } from '@/types/broadcastQueue';

function extractApiError(error: unknown, fallback: string): string {
  const maybeAxios = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };

  return (
    maybeAxios.response?.data?.error ||
    maybeAxios.response?.data?.message ||
    maybeAxios.message ||
    fallback
  );
}

type TestCloneMode = 'snapshot' | 'deep_emulation' | 'channel_matrix';

export function TestCloneSection({
  itemId,
  tenantId,
  currentChannel,
  onSent,
}: {
  itemId: string;
  tenantId?: string;
  currentChannel: string;
  onSent?: () => void | Promise<void>;
}) {
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<SendTestCloneResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<TestCloneMode>('snapshot');
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);

  const { data: channelsData } = useQuery({
    queryKey: ['available-channels', itemId, tenantId],
    queryFn: () => broadcastQueueApi.getAvailableChannels(itemId, tenantId),
    enabled: !!itemId,
    staleTime: 30_000,
  });

  const availableChannels = useMemo<ChannelOption[]>(
    () => channelsData?.channels || [],
    [channelsData?.channels]
  );

  useEffect(() => {
    if (mode !== 'channel_matrix') {
      setSelectedChannels([]);
    }
  }, [mode]);

  const handleSend = async () => {
    setIsSending(true);
    setError(null);
    setResult(null);

    try {
      const response = await broadcastQueueApi.sendTestClone(
        itemId,
        tenantId,
        mode,
        mode === 'channel_matrix' ? selectedChannels : undefined
      );
      setResult(response);

      if (!response.success && (!response.results || response.results.length <= 1)) {
        setError(response.error || response.message || 'Не удалось отправить контрольную копию');
      }
      if (response.clone_item_id) {
        await onSent?.();
      }
    } catch (sendError) {
      setError(extractApiError(sendError, 'Не удалось отправить контрольную копию'));
    } finally {
      setIsSending(false);
    }
  };

  if (isDemoNotify()) return null;

  return (
    <TestSelfSection
      currentChannel={currentChannel}
      mode={mode}
      onModeChange={setMode}
      onSend={handleSend}
      availableChannels={availableChannels}
      selectedChannels={selectedChannels}
      onSelectedChannelsChange={setSelectedChannels}
      isSending={isSending}
      error={error}
      result={result}
    />
  );
}
